import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { EmptyState, PillButton, Card } from '../ui';
import { ReactComponent as OfficeBuildingIcon } from '../assets/icons/office-building.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import './InviteAccept.css';

/**
 * Página pública de aceptación de invitación (`/invite/:token`), fuera del
 * shell, igual que `/s/:id`. Sin sesión solo enseña la vista previa y manda
 * a /login guardando el token; con sesión, el botón acepta directamente.
 */
function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState('loading'); // loading | preview | error
  const [preview, setPreview] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');

  const hasSession = Boolean(localStorage.getItem('authToken'));

  useEffect(() => {
    api.get(`/org/invitations/${token}/preview`)
      .then((res) => { setPreview(res.data); setState('preview'); })
      .catch(() => setState('error'));
  }, [token]);

  const goToLogin = useCallback(() => {
    sessionStorage.setItem('pendingInvite', token);
    navigate('/login');
  }, [token, navigate]);

  const accept = useCallback(async () => {
    setAccepting(true);
    setAcceptError('');
    try {
      const res = await api.post(`/org/invitations/${token}/accept`, { acceptAsCurrentAccount: true });
      const orgId = res.data?.member?.orgId;
      navigate(orgId ? `/admin/${orgId}/members` : '/');
    } catch (err) {
      setAcceptError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setAccepting(false);
    }
  }, [token, navigate, t]);

  return (
    <div className="sgc-invite-accept">
      <Card className="sgc-invite-accept__box">
        {state === 'loading' && <EmptyState icon={OfficeBuildingIcon} title={t('common.loading')} />}

        {state === 'error' && (
          <EmptyState icon={AlertTriangleIcon} title={t('inviteAccept.notFound')} />
        )}

        {state === 'preview' && (
          <div className="sgc-invite-accept__content">
            <EmptyState
              icon={OfficeBuildingIcon}
              title={t('inviteAccept.title', { orgName: preview.orgName })}
              description={t('inviteAccept.subtitle', { roleName: preview.roleName, email: preview.email })}
            />
            {acceptError && <p className="sgc-invite-accept__error">{acceptError}</p>}
            {hasSession ? (
              <PillButton variant="primary" disabled={accepting} loading={accepting} onClick={accept}>
                {t('inviteAccept.join', { orgName: preview.orgName })}
              </PillButton>
            ) : (
              <PillButton variant="primary" onClick={goToLogin}>
                {t('inviteAccept.loginToJoin')}
              </PillButton>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

export default InviteAccept;
